export type Subscription = {
  trialPendingAction?: "end_of_trial" | "immediate" | "undo" | "convert" | null;
  type: Type;
  tier: Tier;
  trialEnd: string | null;
  trialStartedAt: string | null;
  trialSource: "billing_trial" | "paid_upgrade_overlay" | null;
  trialConversionStatus:
    | "cancel_scheduled"
    | "active"
    | "converting"
    | "payment_pending"
    | "converted"
    | "cancelled"
    | "failed"
    | null;
};

/**
 * A subscription is "in trial" iff trialEnd is in the future.
 * trialEnd is cleared on conversion/cancellation, so once past, this helper returns false.
 */
export const isTrialing = (s: Pick<Subscription, "trialEnd">): boolean =>
  s.trialEnd != null && new Date(s.trialEnd) > new Date();

// if type is all_access, all_access_v2, all_access_v3, or all_access_v4, tier will be free | paid+
export type Type =
  | "euroleague"
  | "eurocup"
  | "bcl"
  | "acb"
  | "bsl"
  | "gbl"
  | "lba"
  | "bbl"
  | "lnb"
  | "aba"
  | "lkl"
  | "vtb"
  | "bnxt"
  | "israel"
  | "nfl"
  | "nba"
  | "mlb"
  | "epl"
  | "all_access"
  | "all_access_v2"
  | "all_access_v3"
  | "all_access_v4"
  | "nhl"
  | "wnba"
  | "cba"
  | "ncaaf"
  | "ncaab"
  | "ncaaw"
  | "mma"
  | "cs"
  | "valorant"
  | "fifa"
  | "laliga"
  | "seriea"
  | "ucl"
  | "bundesliga"
  | "ligue1"
  | "mls"
  | "lol"
  | "dota2"
  | "pga"
  | "atp"
  | "wta"
  | "f1"
  | "cbb"
  | "lab"
  | "stories_nba"
  | "stories_wnba"
  | "stories_mlb"
  | "stories_nhl"
  | "stories_nfl"
  | "stories_soccer";
export type Tier = "free" | "paid" | "paid+";

export type User = {
  email: string;
  apiKey: string;
  stripeCustomerId: string | null;
  isEnterprise?: boolean;
  subscriptions: Subscription[];
};

export type StripeSubscriptionDetail = {
  billingInterval?: "month" | "year";
  trialBillingInterval?: "month" | "year";
  amount?: number;
  currency?: string;
  scheduledChange?: { effectiveAt: string } | null;
  type: Type;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  status: string;
  trialEnd: string | null;
};

export type StripeInvoice = {
  invoice_number: string | null;
  date: string;
  status: string | null;
  currency: string;
  amount: number;
  url: string | null;
  period_start?: string | null;
  period_end?: string | null;
  payment_processing?: boolean;
  action?: "view" | "pay" | null;
};

export type StripeInvoicePage = {
  data: StripeInvoice[];
  meta: {
    has_more: boolean;
    next_cursor: string | null;
  };
};

export type AppContextType = {
  user: User | null;
  setUser: (u: User | null) => void;
};

// Webhook types
export type WebhookEndpoint = {
  id: string;
  url: string;
  description: string | null;
  active: boolean;
  event_types: string[];
  filters: Record<string, unknown> | null;
  consecutive_failures: number;
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
};

export type WebhookEndpointWithSecret = WebhookEndpoint & {
  secret: string;
};

export type WebhookEventType = {
  type: string;
  description: string;
  sport: string;
  free: boolean;
  available: boolean;
};

export type WebhookDelivery = {
  id: number;
  event_id: string;
  endpoint_id: string;
  status: string;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  last_response_status: number | null;
  last_response_body: string | null;
  last_error: string | null;
  delivered_at: string | null;
  duration_ms: number | null;
  created_at: string;
  updated_at: string;
  event?: WebhookEvent;
};

export type WebhookEvent = {
  id: string;
  type: string;
  sport: string;
  game_id: number | null;
  payload?: Record<string, unknown>;
  created_at: string;
};

export type WebhookUsage = {
  deliveries_this_month: number;
  deliveries_limit: number;
  endpoints_count: number;
  endpoints_limit: number;
};

export type BillingAttentionItem = {
  token: string;
  type: Type;
  tier: Tier;
  status: string;
  pendingUpdate: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  cancellationMode: "immediate" | "period_end" | "revert_trial" | null;
  billingLocked: boolean;
  invoices: {
    number: string | null;
    amount: number;
    currency: string;
    url: string | null;
    paymentKey?: string;
    prepareToken?: string;
    periodStart: string;
    periodEnd: string;
  }[];
};
